import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import type { PortalConfig } from "./config";

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

/** GET from the portal API with the signed-in user's token. */
export async function apiGet<T>(config: PortalConfig, token: string, path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${config.apiUrl}${path}`, { headers: { authorization: `Bearer ${token}` }, signal });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(res.status, body.error ?? `API returned ${res.status}`);
  }
  return (await res.json()) as T;
}

/** POST JSON to the portal API with the signed-in user's token. */
export async function apiPost<T>(config: PortalConfig, token: string, path: string, body: unknown): Promise<T> {
  return apiSend<T>(config, token, "POST", path, body);
}

/** Send JSON with any method (POST, PUT, PATCH, DELETE). */
export async function apiSend<T>(config: PortalConfig, token: string, method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${config.apiUrl}${path}`, {
    method,
    headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new ApiError(res.status, data.error ?? `API returned ${res.status}`);
  return data;
}

export interface Loaded<T> {
  data: T | null;
  error: ApiError | Error | null;
  loading: boolean;
}

/** Loads `path` whenever it (or `reload`) changes; the previous result stays visible while loading. */
export function useApi<T>(config: PortalConfig, path: string | null, reload = 0): Loaded<T> {
  const auth = useAuth();
  const token = auth.user?.id_token;
  const [state, setState] = useState<Loaded<T>>({ data: null, error: null, loading: !!path });
  useEffect(() => {
    if (!path || !token) return;
    const ctrl = new AbortController();
    setState((s) => ({ ...s, loading: true, error: null }));
    apiGet<T>(config, token, path, ctrl.signal)
      .then((data) => setState({ data, error: null, loading: false }))
      .catch((error: unknown) => {
        if (ctrl.signal.aborted) return;
        setState((s) => ({ ...s, error: error instanceof Error ? error : new Error(String(error)), loading: false }));
      });
    return () => ctrl.abort();
  }, [config, token, path, reload]);
  return state;
}
